import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert as MuiAlert,
  Chip
} from '@mui/material';
import AasMessage from './AasMessage';
import AasInputBar from './AasInputBar';
import { createAasSession, getActiveAasSession, sendAasMessage } from '../../../services/aasApi';

const STARTER_PROMPTS = [
  'Investigate this ticket',
  'Classify this issue',
  'Check platform health'
];

const AasChatPanel = ({ ticketId, token }) => {
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Initialize session on mount
  useEffect(() => {
    let cancelled = false;

    const initSession = async () => {
      setInitializing(true);
      setError(null);

      try {
        // Check for existing active session
        const activeResult = await getActiveAasSession(ticketId, token);
        if (!cancelled && activeResult.session) {
          setSession(activeResult.session);
          setMessages(activeResult.session.messages || []);
          setInitializing(false);
          return;
        }

        // Create new session
        const createResult = await createAasSession(ticketId, token);
        if (!cancelled && createResult.session) {
          setSession(createResult.session);
          setMessages(createResult.session.messages || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.msg || err.message || 'Failed to initialize AAS session');
        }
      } finally {
        if (!cancelled) {
          setInitializing(false);
        }
      }
    };

    initSession();
    return () => { cancelled = true; };
  }, [ticketId, token]);

  const handleSend = async (text) => {
    if (!session || loading) return;
    setLoading(true);
    setError(null);

    // Optimistically add staff message
    const staffMsg = {
      _id: 'staff-' + Date.now(),
      role: 'staff',
      content: text,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, staffMsg]);

    try {
      const result = await sendAasMessage(session._id, text, token);

      if (result.aasMessage) {
        setMessages(prev => [...prev, result.aasMessage]);
      }
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to send message');
      // Add error message from AAS
      setMessages(prev => [...prev, {
        _id: 'error-' + Date.now(),
        role: 'aas',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  if (initializing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, minHeight: 300 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Initializing AAS...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 500 }}>
      {error && (
        <MuiAlert severity="error" sx={{ mx: 1, mt: 1 }} onClose={() => setError(null)}>
          {error}
        </MuiAlert>
      )}

      {/* Messages area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              AAS is ready to investigate this ticket.
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 2 }}>
              AAS uses metadata only — it never accesses clinical content.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              {STARTER_PROMPTS.map((prompt) => (
                <Chip
                  key={prompt}
                  label={prompt}
                  variant="outlined"
                  size="small"
                  onClick={() => handleSend(prompt)}
                  disabled={loading}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#e3f2fd' } }}
                />
              ))}
            </Box>
          </Box>
        )}

        {messages.map((msg, idx) => (
          <AasMessage key={msg._id || idx} message={msg} />
        ))}

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1, py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              AAS is investigating (may consult TASS for clinical analysis)...
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <AasInputBar
        onSend={handleSend}
        loading={loading}
        disabled={!session || session.status === 'closed'}
      />
    </Box>
  );
};

export default AasChatPanel;
